import { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/adminService';

// Helper to parse dates safely
const toIsoDate = (dateString) => {
    if (!dateString) return null;
    const parsed = new Date(`${dateString}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const useCustomerPricing = (selectedCustomer, updateLocalCustomer) => {
    const [customerPricingDetails, setCustomerPricingDetails] = useState(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricingError, setPricingError] = useState(null);
    const [pricingSuccess, setPricingSuccess] = useState('');

    // Form States
    const [basePriceInput, setBasePriceInput] = useState('');
    const [baseSaving, setBaseSaving] = useState(false);

    const [overrideForm, setOverrideForm] = useState({
        amount: '',
        startDate: '',
        endDate: '',
        notes: '',
        revertType: 'base',
        revertValue: ''
    });
    const [overrideSaving, setOverrideSaving] = useState(false);
    const [clearingOverride, setClearingOverride] = useState(false);

    const [billingDateInput, setBillingDateInput] = useState('');
    const [billingCycleStartInput, setBillingCycleStartInput] = useState('');
    const [billingSaving, setBillingSaving] = useState(false);
    const [billingSuccess, setBillingSuccess] = useState('');
    const [billingError, setBillingError] = useState(null);

    const [serviceActivationDateInput, setServiceActivationDateInput] = useState('');
    const [serviceActivationSaving, setServiceActivationSaving] = useState(false);
    const [serviceActivationSuccess, setServiceActivationSuccess] = useState('');
    const [serviceActivationError, setServiceActivationError] = useState(null);

    const [avcIdInput, setAvcIdInput] = useState('');
    const [avcIdSaving, setAvcIdSaving] = useState(false);
    const [avcIdSuccess, setAvcIdSuccess] = useState('');
    const [avcIdError, setAvcIdError] = useState(null);

    // Initialize form state when customer changes
    useEffect(() => {
        if (selectedCustomer) {
            // Billing Dates
            if (selectedCustomer.nextBillingDate) {
                setBillingDateInput(selectedCustomer.nextBillingDate.slice(0, 10));
            } else {
                setBillingDateInput('');
            }

            if (selectedCustomer.billingCycleStart) {
                setBillingCycleStartInput(selectedCustomer.billingCycleStart.slice(0, 10));
            } else {
                setBillingCycleStartInput('');
            }

            // Service Activation Date
            if (selectedCustomer.serviceDate) {
                setServiceActivationDateInput(selectedCustomer.serviceDate.slice(0, 10));
            } else if (selectedCustomer.serviceStartDate) {
                setServiceActivationDateInput(selectedCustomer.serviceStartDate.slice(0, 10));
            } else {
                setServiceActivationDateInput('');
            }

            // AVC ID
            setAvcIdInput(selectedCustomer.avcId || '');

            // Load pricing details
            loadCustomerPricing(selectedCustomer);
        } else {
            resetState();
        }
    }, [selectedCustomer?.UID]); // Depend on ID to reload if customer object updates

    const resetState = () => {
        setCustomerPricingDetails(null);
        setPricingError(null);
        setPricingSuccess('');
        setBasePriceInput('');
        setOverrideForm({
            amount: '',
            startDate: '',
            endDate: '',
            notes: '',
            revertType: 'base',
            revertValue: ''
        });
        setBillingDateInput('');
        setBillingCycleStartInput('');
        setServiceActivationDateInput('');
        setAvcIdInput('');
    };

    const buildOverrideFormState = (override) => ({
        amount: override?.amount !== undefined && override?.amount !== null ? override.amount.toString() : '',
        startDate: override?.startDate ? override.startDate.slice(0, 10) : '',
        endDate: override?.endDate ? override.endDate.slice(0, 10) : '',
        notes: override?.notes || '',
        revertType: typeof override?.revertTo === 'number'
            ? 'custom'
            : (override?.revertTo || 'base'),
        revertValue: typeof override?.revertTo === 'number'
            ? override.revertTo.toString()
            : ''
    });

    const loadCustomerPricing = async (customer) => {
        if (!customer?.UID) return;

        setPricingLoading(true);
        setPricingError(null);
        try {
            const data = await adminService.getCustomerPricing(customer.UID, customer.email);
            const pricingPayload = data.pricing || {};

            setCustomerPricingDetails({
                customerId: data.customerId || customer.UID,
                plan: data.plan || customer.plan,
                monthlyFee: data.monthlyFee ?? customer.monthlyFee,
                pricing: pricingPayload
            });

            const baseAmount = typeof pricingPayload.baseAmount === 'number'
                ? pricingPayload.baseAmount
                : (data.monthlyFee ?? customer.monthlyFee ?? null);

            setBasePriceInput(
                baseAmount !== null && baseAmount !== undefined && baseAmount !== ''
                    ? Number(baseAmount).toFixed(2)
                    : ''
            );

            setOverrideForm(buildOverrideFormState(pricingPayload.override));

            // If the API returned updated customer details, update parent
            if (data.customer && updateLocalCustomer) {
                // Only if we have the updateLocalCustomer callback
                // This avoids stale closures if handled correctly
            }
        } catch (error) {
            console.error('Error loading pricing:', error);
            setPricingError(error.message);
            // Fallback
            setCustomerPricingDetails({
                customerId: customer.UID,
                pricing: customer.pricing || null
            });
        } finally {
            setPricingLoading(false);
        }
    };

    const updatePricingFromResponse = (pricingPayload) => {
        if (!pricingPayload) return;
        setCustomerPricingDetails(prev => ({
            ...prev,
            pricing: pricingPayload
        }));
        if (typeof pricingPayload.baseAmount === 'number') {
            setBasePriceInput(Number(pricingPayload.baseAmount).toFixed(2));
        }
        setOverrideForm(buildOverrideFormState(pricingPayload.override));
    };

    const saveBasePrice = async () => {
        if (!selectedCustomer) return;
        const amount = parseFloat(basePriceInput);
        if (Number.isNaN(amount) || amount <= 0) {
            setPricingError('Enter a valid base price > 0');
            return;
        }

        setBaseSaving(true);
        setPricingError(null);
        setPricingSuccess('');

        try {
            const data = await adminService.setCustomerBasePricing({
                customerId: selectedCustomer.UID,
                customerEmail: selectedCustomer.email,
                baseAmount: amount,
                updatedBy: 'admin-portal'
            });
            updatePricingFromResponse(data.pricing);
            setPricingSuccess('Base price saved.');
        } catch (err) {
            setPricingError(err.message);
        } finally {
            setBaseSaving(false);
        }
    };

    const saveOverride = async () => {
        if (!selectedCustomer) return;
        const amount = parseFloat(overrideForm.amount);
        if (Number.isNaN(amount) || amount <= 0) {
            setPricingError('Enter valid override amount > 0');
            return;
        }

        let revertToValue = overrideForm.revertType;
        if (overrideForm.revertType === 'custom') {
            revertToValue = parseFloat(overrideForm.revertValue);
            if (Number.isNaN(revertToValue) || revertToValue <= 0) {
                setPricingError('Enter valid revert amount');
                return;
            }
        }

        setOverrideSaving(true);
        setPricingError(null);
        setPricingSuccess('');

        try {
            const data = await adminService.setCustomerPricingOverride({
                customerId: selectedCustomer.UID,
                customerEmail: selectedCustomer.email,
                override: {
                    amount,
                    startDate: overrideForm.startDate ? new Date(`${overrideForm.startDate}T00:00:00Z`).toISOString() : null,
                    endDate: overrideForm.endDate ? new Date(`${overrideForm.endDate}T23:59:59Z`).toISOString() : null,
                    revertTo: revertToValue,
                    notes: overrideForm.notes || null
                },
                updatedBy: 'admin-portal'
            });
            updatePricingFromResponse(data.pricing);
            setPricingSuccess('Override saved.');
        } catch (err) {
            setPricingError(err.message);
        } finally {
            setOverrideSaving(false);
        }
    };

    const clearOverride = async () => {
        if (!selectedCustomer) return;
        setClearingOverride(true);
        try {
            const data = await adminService.clearCustomerPricingOverride(selectedCustomer.UID, selectedCustomer.email);
            updatePricingFromResponse(data.pricing);
            setPricingSuccess('Override cleared.');
        } catch (err) {
            setPricingError(err.message);
        } finally {
            setClearingOverride(false);
        }
    };

    const saveBillingDate = async () => {
        if (!selectedCustomer || !billingDateInput) return;

        const isoNext = toIsoDate(billingDateInput);
        if (!isoNext) {
            setBillingError('Invalid date');
            return;
        }

        setBillingSaving(true);
        setBillingError(null);

        try {
            const payload = {
                customerId: selectedCustomer.UID,
                customerEmail: selectedCustomer.email,
                nextBillingDate: isoNext,
                updatedBy: 'admin-portal'
            };
            if (billingCycleStartInput) {
                payload.billingCycleStartDate = toIsoDate(billingCycleStartInput);
            }

            await adminService.setCustomerBillingDate(payload);
            setBillingSuccess('Billing schedule updated.');

            // Update local customer object through callback
            if (updateLocalCustomer) {
                updateLocalCustomer({
                    UID: selectedCustomer.UID,
                    nextBillingDate: isoNext,
                    billingCycleStart: payload.billingCycleStartDate || selectedCustomer.billingCycleStart
                });
            }
        } catch (err) {
            setBillingError(err.message);
        } finally {
            setBillingSaving(false);
        }
    };

    const saveServiceActivationDate = async () => {
        if (!selectedCustomer || !serviceActivationDateInput) return;
        const isoDate = toIsoDate(serviceActivationDateInput);

        setServiceActivationSaving(true);
        setServiceActivationError(null);
        try {
            const result = await adminService.updateServiceActivationDate({
                customerId: selectedCustomer.UID,
                customerEmail: selectedCustomer.email,
                serviceActivationDate: isoDate,
                adminUser: 'admin-portal'
            });

            if (result.success) {
                setServiceActivationSuccess('Updated successfully.');
                if (result.customer && updateLocalCustomer) {
                    updateLocalCustomer(result.customer);
                }
            } else {
                throw new Error(result.error);
            }
        } catch (err) {
            setServiceActivationError(err.message);
        } finally {
            setServiceActivationSaving(false);
        }
    };

    const saveAvcId = async () => {
        if (!selectedCustomer) return;

        setAvcIdSaving(true);
        setAvcIdError(null);
        setAvcIdSuccess('');

        try {
            const result = await adminService.updateCustomer({
                customerId: selectedCustomer.UID,
                customerEmail: selectedCustomer.email,
                updates: {
                    avcId: avcIdInput
                },
                adminUser: 'admin-portal'
            });

            if (result.success) {
                setAvcIdSuccess('AVC ID updated successfully.');
                if (result.customer && updateLocalCustomer) {
                    updateLocalCustomer(result.customer);
                }
            } else {
                throw new Error(result.error || 'Failed to update AVC ID');
            }
        } catch (err) {
            setAvcIdError(err.message);
        } finally {
            setAvcIdSaving(false);
        }
    };

    return {
        customerPricingDetails,
        pricingLoading,
        pricingError,
        pricingSuccess,

        basePriceInput,
        setBasePriceInput,
        baseSaving,
        saveBasePrice,

        overrideForm,
        setOverrideForm, // Exposed if needed
        handleOverrideInputChange: (f, v) => setOverrideForm(prev => ({ ...prev, [f]: v })),
        overrideSaving,
        saveOverride,

        clearingOverride,
        clearOverride,

        billingDateInput,
        setBillingDateInput,
        billingCycleStartInput,
        setBillingCycleStartInput,
        billingSaving,
        billingSuccess,
        billingError,
        saveBillingDate,

        serviceActivationDateInput,
        setServiceActivationDateInput,
        serviceActivationSaving,
        serviceActivationSuccess,
        serviceActivationError,
        saveServiceActivationDate,

        avcIdInput,
        setAvcIdInput,
        avcIdSaving,
        avcIdSuccess,
        avcIdError,
        saveAvcId
    };
};
